import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { TResponse } from "../../../types/common";
import { INotificationPreferences } from "../../../types/api/integrations";
import { getNotificationPreferences, updateNotificationPreferences } from "./callbacks";

export const useNotificationPreferences = (
  options?: Omit<
    UseQueryOptions<TResponse<INotificationPreferences>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponse<INotificationPreferences>, Error> => {
  return useQuery<TResponse<INotificationPreferences>, Error>({
    queryKey: [API_ENDPOINTS.NOTIFICATION_PREFERENCES],
    queryFn: async () => {
      const res = await getNotificationPreferences();
      return res.data;
    },
    staleTime: 60000,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useUpdateNotificationPreferences = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<INotificationPreferences>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<
    TResponse<INotificationPreferences>,
    unknown,
    INotificationPreferences
  > = {}
): UseMutationResult<TResponse<INotificationPreferences>, unknown, INotificationPreferences> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<INotificationPreferences>, unknown, INotificationPreferences>({
    mutationFn: async (data: INotificationPreferences) => {
      const res = await updateNotificationPreferences(data);
      return res.data;
    },
    onSuccess: (res, ...rest) => {
      queryClient.setQueryData([API_ENDPOINTS.NOTIFICATION_PREFERENCES], res);
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};
